/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { Button } from './button';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { VoiceAgentDialog } from './voice-agent-dialog';

export function FloatingVoiceButton() {
	const [showMainDialog, setShowMainDialog] = React.useState(false);
	const [showMiniDialog, setShowMiniDialog] = React.useState(false);
	const [isConnected, setIsConnected] = React.useState(false);
	const isTransitioningRef = React.useRef(false);
	const justOpenedRef = React.useRef(false);

	// Create a ref to hold the minimize function
	const minimizeDialogRef = React.useRef({
		minimize: () => {
			// console.log('🔄 [FloatingVoiceButton] Minimize requested via ref');
			if (isConnected) {
				setShowMiniDialog(true);
				setShowMainDialog(false);
			}
		},
	});

	const handleOpen = React.useCallback((event?: React.MouseEvent) => {
		event?.preventDefault();
		// console.log('FloatingVoiceButton - Opening dialog');
		setShowMainDialog(true);
		setShowMiniDialog(false);
		justOpenedRef.current = true;
		// Reset the justOpened flag after a longer delay
		setTimeout(() => {
			justOpenedRef.current = false;
		}, 2000);
	}, []);

	const handleClose = React.useCallback(() => {
		// console.log('🔄 [FloatingVoiceButton] Close requested');

		if (isConnected) {
			// console.log(
			// 	'🔄 [FloatingVoiceButton] Connected - minimizing first'
			// );
			setShowMiniDialog(true);
			setShowMainDialog(false);
		} else {
			// console.log('🔄 [FloatingVoiceButton] Closing directly');
			setShowMainDialog(false);
			setShowMiniDialog(false);
			setIsConnected(false);
		}
	}, [isConnected]);

	const handleMinimize = React.useCallback(() => {
		// console.log('🔄 [FloatingVoiceButton] Minimize requested');
		setShowMiniDialog(true);
		setShowMainDialog(false);
	}, []);

	const handleMaximize = React.useCallback(() => {
		// console.log('🔄 [FloatingVoiceButton] Maximize requested');
		setShowMainDialog(true);
		setShowMiniDialog(false);
	}, []);

	const handleNavigationStart = React.useCallback(async () => {
		// console.log('🎯 [FloatingVoiceButton] Navigation requested');

		// Simply minimize and close
		if (isConnected) {
			// console.log(
			// 	'🔄 [FloatingVoiceButton] Minimizing before navigation'
			// );
			setShowMiniDialog(true);
			setShowMainDialog(false);
		}

		// Close after a short delay
		setTimeout(() => {
			// console.log('🔄 [FloatingVoiceButton] Closing all dialogs');
			setShowMiniDialog(false);
			setShowMainDialog(false);
			setIsConnected(false);
			// console.log('✅ [FloatingVoiceButton] Navigation complete');
		}, 300);
	}, [isConnected]);

	const handleConnectionChange = React.useCallback((connected: boolean) => {
		setIsConnected(connected);
	}, []);

	return (
		<>
			{/* Ask Prometheus Button */}
			<AnimatePresence>
				{!showMainDialog && !showMiniDialog && (
					<motion.div
						initial={{ opacity: 0, scale: 0.9, y: 20 }}
						animate={{ opacity: 1, scale: 1, y: 0 }}
						exit={{ opacity: 0, scale: 0.9, y: 20 }}
						transition={{ duration: 0.2 }}
						className={cn('fixed bottom-4 left-6 z-[9999]')}
					>
						<Button
							onClick={handleOpen}
							type='button'
							className='bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 
                                    rounded-full px-6 py-6 text-base font-medium transition-all duration-200
                                    text-gray-50 dark:text-white shadow-[0_0_50px_8px_rgba(236,72,153,0.3)]
                                    hover:shadow-[0_0_70px_10px_rgba(236,72,153,0.5)]'
						>
							<motion.span
								animate={{ scale: [1, 1.1, 1] }}
								transition={{ repeat: Infinity, duration: 2 }}
								className='mr-2'
							>
								📞
							</motion.span>
							Ask Prometheus
						</Button>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Voice Agent Dialog */}
			<VoiceAgentDialog
				isOpen={showMainDialog}
				onClose={handleClose}
				onConnectionChange={handleConnectionChange}
				onMinimize={handleMinimize}
				onMaximize={handleMaximize}
				showMini={showMiniDialog}
				isTransitioning={isTransitioningRef.current}
				minimizeDialogRef={minimizeDialogRef}
			/>
		</>
	);
}
